import React from 'react';

interface OrganizationSchemaProps {
  name?: string;
  url?: string;
  logo?: string;
  description?: string;
  email?: string;
  telephone?: string;
  address?: {
    streetAddress?: string;
    addressLocality?: string;
    postalCode?: string;
    addressCountry?: string;
  };
  geo?: {
    latitude?: number;
    longitude?: number;
  };
}

interface LocalBusinessSchemaProps extends OrganizationSchemaProps {
  openingHours?: string;
  priceRange?: string;
  serviceType?: string[];
}

interface EventSchemaProps {
  name: string;
  description: string;
  startDate: string;
  endDate?: string;
  location: {
    name: string;
    address: {
      streetAddress: string;
      addressLocality: string;
      postalCode: string;
      addressCountry: string;
    };
  };
  organizer: {
    name: string;
    url: string;
  };
  eventStatus?: 'EventScheduled' | 'EventCancelled' | 'EventPostponed';
  eventAttendanceMode?: 'OfflineEventAttendanceMode' | 'OnlineEventAttendanceMode' | 'MixedEventAttendanceMode';
}

interface ArticleSchemaProps {
  headline: string;
  description: string;
  author: {
    name: string;
    url?: string;
  };
  publisher: {
    name: string;
    logo: {
      url: string;
    };
  };
  datePublished: string;
  dateModified?: string;
  image?: string;
  url: string;
}

interface BreadcrumbSchemaProps {
  items: Array<{
    name: string;
    url: string;
  }>;
}

export const OrganizationSchema: React.FC<OrganizationSchemaProps> = ({
  name = "BeArt Művészetterápia",
  url = "https://beart.hu",
  logo = "https://beart.hu/images/beart-transparent-logo.webp",
  description = "Művészetterápiás foglalkozások, art coaching, családállítás, coaching, önismeret - művészetterápiás csoportok, egyéni és csoportos alkalmak",
  email = "info@beart.hu",
  telephone,
  address = {
    addressLocality: "Budapest",
    addressCountry: "HU"
  },
  geo = {
    latitude: 47.4979,
    longitude: 19.0402
  }
}) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": name,
    "url": url,
    "logo": logo,
    "description": description,
    "address": {
      "@type": "PostalAddress",
      "addressLocality": address.addressLocality,
      "addressCountry": address.addressCountry,
      ...(address.streetAddress && { "streetAddress": address.streetAddress }),
      ...(address.postalCode && { "postalCode": address.postalCode })
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": geo.latitude,
      "longitude": geo.longitude
    },
    "contactPoint": {
      "@type": "ContactPoint",
      "contactType": "customer service",
      "email": email,
      ...(telephone && { "telephone": telephone })
    },
    "sameAs": [
      "https://www.facebook.com/beartmuveszetterapia",
      "https://www.instagram.com/beart_muveszetterapia"
    ]
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
};

export const LocalBusinessSchema: React.FC<LocalBusinessSchemaProps> = ({
  name = "BeArt Művészetterápia",
  url = "https://beart.hu",
  logo = "https://beart.hu/images/beart-transparent-logo.webp",
  description = "Művészetterápiás csoportok, egyéni és csoportos alkalmak, art coaching, önismeret",
  email = "info@beart.hu",
  telephone,
  address = {
    addressLocality: "Budapest",
    addressCountry: "HU"
  },
  geo = {
    latitude: 47.4979,
    longitude: 19.0402
  },
  openingHours = "Mo-Fr 09:00-18:00",
  priceRange = "$$",
  serviceType = ["Művészetterápia", "Art Coaching", "Önismeret", "Családállítás"]
}) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": name,
    "image": logo,
    "description": description,
    "url": url,
    "address": {
      "@type": "PostalAddress",
      "addressLocality": address.addressLocality,
      "addressCountry": address.addressCountry,
      ...(address.streetAddress && { "streetAddress": address.streetAddress }),
      ...(address.postalCode && { "postalCode": address.postalCode })
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": geo.latitude,
      "longitude": geo.longitude
    },
    "openingHours": openingHours,
    "priceRange": priceRange,
    "serviceType": serviceType,
    "contactPoint": {
      "@type": "ContactPoint",
      "contactType": "customer service",
      "email": email,
      ...(telephone && { "telephone": telephone })
    }
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
};

export const EventSchema: React.FC<EventSchemaProps> = ({
  name,
  description,
  startDate,
  endDate,
  location,
  organizer,
  eventStatus = "EventScheduled",
  eventAttendanceMode = "OfflineEventAttendanceMode"
}) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Event",
    "name": name,
    "description": description,
    "startDate": startDate,
    ...(endDate && { "endDate": endDate }),
    "location": {
      "@type": "Place",
      "name": location.name,
      "address": {
        "@type": "PostalAddress",
        "streetAddress": location.address.streetAddress,
        "addressLocality": location.address.addressLocality,
        "postalCode": location.address.postalCode,
        "addressCountry": location.address.addressCountry
      }
    },
    "organizer": {
      "@type": "Organization",
      "name": organizer.name,
      "url": organizer.url
    },
    "eventStatus": eventStatus,
    "eventAttendanceMode": eventAttendanceMode
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
};

export const ArticleSchema: React.FC<ArticleSchemaProps> = ({
  headline,
  description,
  author,
  publisher,
  datePublished,
  dateModified,
  image,
  url
}) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": headline,
    "description": description,
    "author": {
      "@type": "Person",
      "name": author.name,
      ...(author.url && { "url": author.url })
    },
    "publisher": {
      "@type": "Organization",
      "name": publisher.name,
      "logo": {
        "@type": "ImageObject",
        "url": publisher.logo.url
      }
    },
    "datePublished": datePublished,
    ...(dateModified && { "dateModified": dateModified }),
    ...(image && { "image": image }),
    "url": url
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
};

export const BreadcrumbSchema: React.FC<BreadcrumbSchemaProps> = ({ items }) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      "item": item.url
    }))
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
};

export const WebSiteSchema: React.FC<{ url: string; name: string }> = ({
  url = "https://beart.hu",
  name = "BeArt Művészetterápia"
}) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": name,
    "url": url,
    "potentialAction": {
      "@type": "SearchAction",
      "target": {
        "@type": "EntryPoint",
        "urlTemplate": `${url}/search?q={search_term_string}`
      },
      "query-input": "required name=search_term_string"
    }
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
};

export const FAQSchema: React.FC<{ questions: Array<{ question: string; answer: string }> }> = ({
  questions
}) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": questions.map(q => ({
      "@type": "Question",
      "name": q.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": q.answer
      }
    }))
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
};
